// Menší prvky: záložky, dělený panel, strom, stavový řádek, prázdný/chybový stav.
import { h, clear, clamp } from '../dom';
import { t, fmtSize } from '../i18n';
import { icon } from '../icons';

export class IwTabs extends HTMLElement {
  private tabs: { id: string; label: string }[] = [];
  private active = '';
  private head = h('div', { class: 'tabs-head', role: 'tablist' });
  private body = h('div', { class: 'tabs-body' });
  private panels = new Map<string, HTMLElement>();
  onChange: ((id: string) => void) | null = null;

  connectedCallback(): void {
    if (!this.hasChildNodes()) this.append(this.head, this.body);
  }

  setTabs(tabs: { id: string; label: string }[]): void {
    this.tabs = tabs;
    clear(this.head);
    clear(this.body);
    this.panels.clear();
    for (const tab of tabs) {
      const panel = h('div', { class: 'tab-panel', role: 'tabpanel', dataset: { tab: tab.id } });
      this.panels.set(tab.id, panel);
      this.body.appendChild(panel);
      const btn = h('button', { type: 'button', class: 'tab', role: 'tab', dataset: { tab: tab.id } }, tab.label);
      btn.addEventListener('click', () => this.select(tab.id));
      this.head.appendChild(btn);
    }
    this.head.hidden = tabs.length <= 1;
    this.select(this.active && this.panels.has(this.active) ? this.active : (tabs[0]?.id ?? ''));
  }

  panel(id: string): HTMLElement {
    return this.panels.get(id) ?? this.body;
  }

  select(id: string): void {
    this.active = id;
    this.head.querySelectorAll<HTMLElement>('.tab').forEach((b) => {
      const on = b.dataset['tab'] === id;
      b.classList.toggle('active', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    for (const [k, p] of this.panels) p.hidden = k !== id;
    this.onChange?.(id);
  }

  current(): string {
    return this.active;
  }
}
customElements.define('iw-tabs', IwTabs);

/**
 * Dělený panel s táhlem: vedle sebe (výchozí, šířka prvního panelu) nebo nad sebou (`setVertical`, výška prvního panelu).
 * Táhlo jde tahat myší i prstem, posouvat šipkami z klávesnice; dvojklik vrátí `defaultSize`. Velikost se dá uložit přes `onResize`.
 */
export class IwSplit extends HTMLElement {
  readonly first = h('div', { class: 'split-first' });
  readonly second = h('div', { class: 'split-second' });
  private handle = h('div', { class: 'split-handle', role: 'separator', 'aria-orientation': 'vertical', tabindex: '0' });
  private vertical = false;
  /** velikost po dvojkliku na táhlo (0 = bez obnovy) */
  defaultSize = 0;
  /** Šířka platí pro druhý (pravý) panel – podokno podrobností; první se přizpůsobí oknu. */
  fixedSecond = false;
  onResize: ((px: number) => void) | null = null;

  connectedCallback(): void {
    if (this.hasChildNodes()) return;
    this.append(this.first, this.handle, this.second);
    this.handle.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.handle.setPointerCapture(e.pointerId);
      const rect = this.getBoundingClientRect();
      const move = (ev: PointerEvent) => (this.vertical ? this.setHeight(clamp(ev.clientY - rect.top, 120, rect.height - 160)) : this.fixedSecond ? this.setWidth(clamp(rect.right - ev.clientX, 200, rect.width - 240)) : this.setWidth(clamp(ev.clientX - rect.left, 120, rect.width - 200)));
      const up = () => {
        this.handle.removeEventListener('pointermove', move);
        this.handle.removeEventListener('pointerup', up);
        this.onResize?.(this.size());
      };
      this.handle.addEventListener('pointermove', move);
      this.handle.addEventListener('pointerup', up);
    });
    this.handle.addEventListener('dblclick', () => { if (this.defaultSize > 0) { this.setSize(this.defaultSize); this.onResize?.(this.size()); } });
    this.handle.addEventListener('keydown', (e) => {
      const dec = this.vertical ? e.key === 'ArrowUp' : e.key === 'ArrowLeft';
      const inc = this.vertical ? e.key === 'ArrowDown' : e.key === 'ArrowRight';
      if (!dec && !inc) return;
      e.preventDefault();
      const rect = this.getBoundingClientRect();
      const max = this.vertical ? rect.height - 160 : rect.width - 200;
      this.setSize(clamp(this.size() + (inc ? 20 : -20) * (this.fixedSecond ? -1 : 1), 120, Math.max(120, max)));
      this.onResize?.(this.size());
    });
  }

  /** Nad sebou (první panel nahoře, výška) místo vedle sebe. */
  setVertical(on: boolean): void {
    this.vertical = on;
    this.classList.toggle('vertical', on);
    this.handle.setAttribute('aria-orientation', on ? 'horizontal' : 'vertical');
  }

  size(): number {
    const r = (this.fixedSecond && !this.vertical ? this.second : this.first).getBoundingClientRect();
    return this.vertical ? r.height : r.width;
  }

  setSize(px: number): void {
    if (this.vertical) this.setHeight(px); else this.setWidth(px);
  }

  setWidth(px: number): void {
    if (this.fixedSecond) {
      this.second.style.width = px + 'px';
      this.second.style.flex = '0 0 auto';
      this.first.style.width = '';
      this.first.style.flex = '1 1 0';
      return;
    }
    this.first.style.width = px + 'px';
    this.first.style.height = '';
    this.first.style.flex = '0 0 auto';
  }

  setHeight(px: number): void {
    this.first.style.height = px + 'px';
    this.first.style.width = '';
    this.first.style.flex = '0 0 auto';
  }

  /** Schová první panel (strom) i táhlo. */
  setFirstHidden(hidden: boolean): void {
    this.classList.toggle('first-hidden', hidden);
  }

  /** Mobil: první panel se vysouvá přes druhý místo vedle něj. */
  setOverlay(on: boolean): void {
    this.classList.toggle('overlay', on);
  }
}
customElements.define('iw-split', IwSplit);

export interface TreeNode {
  id: string;
  label: string;
  icon?: string;
  children?: TreeNode[] | null; // null = líné načtení
  /** odznak vpravo (počet, „!“) */
  badge?: string;
  /** barevné kolečko před popiskem: ok | warn | bad | info | off */
  dot?: string;
  /** šedý popisek (neaktivní položka) */
  muted?: boolean;
  /** rozbalený smí být jen jeden potomek (rozbalení jiného ostatní sbalí) */
  exclusive?: boolean;
  data?: unknown;
}

export class IwTree extends HTMLElement {
  private nodes: TreeNode[] = [];
  private expanded = new Set<string>();
  private selected: string | null = null;
  onSelect: ((node: TreeNode) => void) | null = null;
  onExpand: ((node: TreeNode) => Promise<TreeNode[]>) | null = null;
  onContext: ((node: TreeNode, e: MouseEvent) => void) | null = null;
  onDrop: ((node: TreeNode, e: DragEvent) => void) | null = null;
  /** změna rozbalení (uložení do předvoleb) */
  onToggle: ((expanded: string[]) => void) | null = null;

  connectedCallback(): void {
    this.setAttribute('role', 'tree');
  }

  /** Rozbalené uzly (pořadí rozbalení). */
  expandedIds(): string[] {
    return Array.from(this.expanded);
  }

  /** Sbalí vše kromě cesty k $keep (vybraný uzel zůstane vidět). */
  collapseAll(keep: string[] = []): void {
    this.expanded = new Set(keep);
    this.render();
    this.onToggle?.(this.expandedIds());
  }

  /** U rodiče s exclusive sbalí sourozence uzlu $id. */
  private collapseSiblings(id: string): void {
    const parent = this.parentOf(id);
    if (!parent?.exclusive) return;
    for (const s of parent.children ?? []) if (s.id !== id) this.expanded.delete(s.id);
  }

  private parentOf(id: string, list: TreeNode[] = this.nodes, parent: TreeNode | null = null): TreeNode | null {
    for (const n of list) {
      if (n.id === id) return parent;
      const p = n.children ? this.parentOf(id, n.children, n) : null;
      if (p) return p;
    }
    return null;
  }

  setNodes(nodes: TreeNode[]): void {
    this.nodes = nodes;
    this.render();
  }

  select(id: string | null): void {
    this.selected = id;
    this.querySelectorAll<HTMLElement>('.tree-row').forEach((r) => r.classList.toggle('selected', r.dataset['id'] === id));
  }

  /** Posune strom, aby byl vybraný uzel vidět (výběr z hledání nebo ze seznamu hluboko ve větvi). */
  reveal(): void {
    this.querySelector<HTMLElement>('.tree-row.selected')?.scrollIntoView({ block: 'nearest' });
  }

  /** Rozbalí uzel; pokud děti ještě nejsou načtené, dotáhne je přes onExpand. */
  async expand(id: string): Promise<void> {
    this.collapseSiblings(id);
    this.expanded.add(id);
    const node = this.find(id);
    if (node && node.children === null && this.onExpand) node.children = await this.onExpand(node);
    this.render();
    this.onToggle?.(this.expandedIds());
  }

  /** Rozbalí celou cestu (např. po navigaci přes drobečky nebo seznam), chybějící úrovně dotáhne. */
  async expandPath(ids: string[]): Promise<void> {
    for (const id of ids) {
      this.collapseSiblings(id);
      this.expanded.add(id);
      const node = this.find(id);
      if (node && node.children === null && this.onExpand) node.children = await this.onExpand(node);
    }
    this.render();
  }

  /** Nastaví děti uzlu z už načteného výpisu (strom odpovídá seznamu); zachová načtené vnuky. */
  setChildren(id: string, children: TreeNode[]): void {
    const node = this.find(id);
    if (!node) return;
    const old = new Map((node.children ?? []).map((c) => [c.id, c]));
    node.children = children.map((c) => {
      const prev = old.get(c.id);
      return prev && prev.children && prev.children.length ? { ...c, children: prev.children } : c;
    });
    this.render();
  }

  find(id: string, list: TreeNode[] = this.nodes): TreeNode | null {
    for (const n of list) {
      if (n.id === id) return n;
      const c = n.children ? this.find(id, n.children) : null;
      if (c) return c;
    }
    return null;
  }

  private render(): void {
    clear(this);
    this.appendChild(this.list(this.nodes, 0));
  }

  private list(nodes: TreeNode[], depth: number): HTMLElement {
    const ul = h('div', { class: 'tree-list', role: depth ? 'group' : null });
    for (const n of nodes) {
      const canExpand = n.children === null || (n.children && n.children.length > 0);
      const open = this.expanded.has(n.id);
      const row = h(
        'div',
        { class: 'tree-row' + (this.selected === n.id ? ' selected' : ''), role: 'treeitem', title: n.label, 'aria-expanded': canExpand ? (open ? 'true' : 'false') : null, dataset: { id: n.id }, style: { paddingLeft: 6 + depth * 14 + 'px' } },
        h('span', { class: 'tree-toggle' + (canExpand ? '' : ' empty') }, canExpand ? icon(open ? 'chevron-down' : 'chevron') : null),
        n.icon ? icon(n.icon, 'icon tree-icon') : null,
        n.dot ? h('span', { class: 'tree-dot ' + n.dot }) : null,
        h('span', { class: 'tree-label' + (n.muted ? ' muted' : '') }, n.label),
        n.badge ? h('span', { class: 'tree-badge' }, n.badge) : null,
      );
      row.querySelector('.tree-toggle')?.addEventListener('click', async (e) => {
        e.stopPropagation();
        if (!canExpand) return;
        if (open) this.expanded.delete(n.id);
        else {
          this.collapseSiblings(n.id);
          this.expanded.add(n.id);
          if (n.children === null && this.onExpand) n.children = await this.onExpand(n);
        }
        this.render();
        this.onToggle?.(this.expandedIds());
      });
      row.addEventListener('click', () => {
        // klik na řádek = vybrat a zároveň rozbalit (jako v Průzkumníku); obsah se zobrazí hned
        this.select(n.id);
        this.onSelect?.(n);
        if (canExpand && !open) void this.expand(n.id);
      });
      row.addEventListener('contextmenu', (e) => {
        if (this.onContext) {
          e.preventDefault();
          this.onContext(n, e);
        }
      });
      row.addEventListener('dragover', (e) => {
        if (this.onDrop) {
          e.preventDefault();
          row.classList.add('drop');
        }
      });
      row.addEventListener('dragleave', () => row.classList.remove('drop'));
      row.addEventListener('drop', (e) => {
        row.classList.remove('drop');
        this.onDrop?.(n, e);
      });
      ul.appendChild(row);
      if (open && n.children && n.children.length) ul.appendChild(this.list(n.children, depth + 1));
    }
    return ul;
  }
}
customElements.define('iw-tree', IwTree);

export function statusBar(...parts: (Node | string)[]): HTMLElement {
  return h('div', { class: 'statusbar' }, parts.map((p) => h('span', { class: 'status-part' }, p)));
}

export function emptyState(text: string, iconName = 'info'): HTMLElement {
  return h('div', { class: 'empty-state' }, icon(iconName, 'icon big'), h('div', null, text));
}

export function errorState(message: string, retry?: () => void, id?: string): HTMLElement {
  return h(
    'div',
    { class: 'error-state' },
    icon('warning', 'icon big'),
    h('div', { class: 'error-title' }, t('core.ui.error')),
    h('div', { class: 'error-msg' }, message),
    id ? h('div', { class: 'error-id mono' }, 'ID: ' + id) : null,
    retry ? h('button', { type: 'button', class: 'btn', onClick: retry }, t('core.ui.retry')) : null,
  );
}

/** Překryv „načítá se“ nad obsahem (rodič musí mít position: relative): kolečko, text a průběh. */
export interface Loader {
  el: HTMLElement;
  /** průběh stahování v bajtech; total null = neznámá velikost (jen počet) */
  progress(loaded: number, total?: number | null): void;
  setText(text: string): void;
  remove(): void;
}

/**
 * Jednotný indikátor načítání pro všechny aplikace. Ukáže se až po $delayMs (u rychlého načtení z mezipaměti neproblikne).
 */
export function loader(text: string = t('core.ui.loading'), delayMs = 150): Loader {
  const label = h('div', { class: 'iw-loader-text' }, text);
  const bar = h('div', { class: 'iw-loader-bar' });
  const track = h('div', { class: 'iw-loader-track', hidden: true }, bar);
  const detail = h('div', { class: 'iw-loader-detail' });
  const el = h('div', { class: 'iw-loader pending', role: 'progressbar', 'aria-label': text }, h('div', { class: 'spinner' }), label, track, detail);
  const timer = window.setTimeout(() => el.classList.remove('pending'), delayMs);
  return {
    el,
    progress(loaded, total = null) {
      if (total && total > 0) {
        const pct = Math.min(100, Math.round((loaded / total) * 100));
        track.hidden = false;
        bar.style.width = pct + '%';
        el.setAttribute('aria-valuenow', String(pct));
        detail.textContent = `${fmtSize(loaded)} / ${fmtSize(total)}`;
      } else {
        detail.textContent = fmtSize(loaded);
      }
    },
    setText(s) {
      label.textContent = s;
    },
    remove() {
      window.clearTimeout(timer);
      el.remove();
    },
  };
}

export function spinner(): HTMLElement {
  return h('div', { class: 'spinner', role: 'progressbar', 'aria-label': t('core.ui.loading') });
}
